import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCrown, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { usePerk } from "../hooks/usePerk";
import { formatTierPrice, AUDIENCE } from "../constants/plans";
import "../styles/PerkGate.css";

/**
 * PerkGate — render `children` only when the signed-in user's tier grants the
 * perk. While plans resolve it renders `loadingFallback` (null by default);
 * when the perk isn't granted it renders `fallback`, defaulting to a friendly
 * <PerkUpgradeNotice/>.
 *
 * Client-side UX gate only — see usePerk for the server-enforcement caveat.
 *
 * Usage:
 *   <PerkGate perk="roommateJobs" loadingFallback={<LoadingScreen />}>
 *     <RoommateFinder />
 *   </PerkGate>
 */
export function PerkGate({ perk, children, fallback, loadingFallback = null }) {
  const { granted, loading } = usePerk(perk);
  if (loading) return loadingFallback;
  if (granted) return <>{children}</>;
  return fallback !== undefined ? fallback : <PerkUpgradeNotice perk={perk} />;
}

/**
 * PerkUpgradeNotice — upgrade prompt shown when a user lacks a perk. Reads the
 * required tier from the perk definition so it always names the right pass and
 * links to the matching upgrade page.
 */
export function PerkUpgradeNotice({ perk, title, description }) {
  const { requiredTier } = usePerk(perk);
  const tierName = requiredTier?.name || "a premium pass";
  const isBusiness = requiredTier?.audience === AUDIENCE.BUSINESS;
  const href = isBusiness ? "/for-businesses" : "/traveler-passes#plus";
  const price = requiredTier ? formatTierPrice(requiredTier) : "";

  return (
    <div className="perk-gate">
      <div className="perk-gate__card">
        <FontAwesomeIcon icon={faCrown} className="perk-gate__icon" />
        <h2 className="perk-gate__title">{title || `A ${tierName} feature`}</h2>
        <p className="perk-gate__desc">
          {description ||
            `This feature is included with ${tierName}${price ? ` (${price})` : ""}. Upgrade your pass to unlock it.`}
        </p>
        <Link to={href} className="marketing-cta perk-gate__cta">
          Explore {tierName} <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </div>
    </div>
  );
}

export default PerkGate;
